<template>
  <div class="notification-wrapper mt-4">
    <button
      @click="toggleNotifications"
      :class="['btn-notification', isSubscribed ? 'active' : '']"
      :disabled="loading"
    >
      <div class="icon-container">
        <i
          :class="[
            'fas',
            isSubscribed ? 'fa-bell-slash' : 'fa-bell shake-animation',
          ]"
        ></i>
      </div>
      <div class="text-container">
        <span class="status-text">{{
          isSubscribed ? "Notifications Enabled" : "Enable Notifications"
        }}</span>
        <span class="sub-text">{{
          isSubscribed ? "Click to unsubscribe" : "Stay updated with your tasks"
        }}</span>
      </div>

      <div v-if="loading" class="spinner-border spinner-border-sm ms-2"></div>
    </button>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { tasksService } from "@/services/tasksService";

export default {
  name: "Subscription",
  data() {
    return {
      loading: false,
    };
  },

  computed: {
    isSubscribed() {
      return this.$store.state.userSubscription;
    },
  },

  async mounted() {
    this.loading = true;
    await this.$store.dispatch("getSubInfos");
    this.loading = false;
  },

  methods: {
    async toggleNotifications() {
      if (this.loading) return;
      this.loading = true;

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (this.isSubscribed && subscription) {
          await this.disableNotifications(subscription);
        } else {
          await this.enableNotifications();
        }
      } catch (err) {
        
        Swal.fire("Error", "Please refresh page", "error");
      } finally {
        this.loading = false;
      }
    },

    async enableNotifications() {
      try {
        const publicKey =
          "BB6xFU5eIzXuhwIu6CZGbBuJ2kr6RcHOXr7mw_PqLQPC6hiFdLMlsoC5Ibk_tb307Irabqn_8LSFkgf-UK6pHXo";
        const convertedKey = this.urlBase64ToUint8Array(publicKey);
        const registration = await navigator.serviceWorker.ready;

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedKey,
        });

        await tasksService.saveSubscription(subscription);

        this.$store.commit("getSubInfos", true);

        Swal.fire({
          icon: "success",
          title: "You will receive updates every hour for overdue tasks.",
          timer: 5000,
        });
      } catch (err) {
        
        Swal.fire("Error", "Permission blocked", "error");
      }
    },

    async disableNotifications(subscription) {
      try {
        let currentSub = subscription;
        const registration = await navigator.serviceWorker.ready;

        if (!currentSub) {
          currentSub = await registration.pushManager.getSubscription();
        }

        if (currentSub) {
          try {
            await currentSub.unsubscribe();
          } catch (err) {
            console.warn("Failed to unsubscribe from browser:", err);
          }

          try {
            await tasksService.unsubscribe(currentSub.endpoint); 
          } catch (err) {
            console.warn("Failed to unsubscribe from server:", err);
          }
        } else {
          try {
            await tasksService.unsubscribeUser();
          } catch (err) {
            console.warn("Failed to unsubscribe from server-only:", err);
          }

          Swal.fire(
            "Info",
            "No active subscription in browser, server subscription has been removed",
            "info",
          );
        }

        this.$store.commit("getSubInfos", false);
      } catch (err) {
        Swal.fire(
          "Error",
          "Something went wrong while disabling notifications.",
          "warning",
        );
        this.$store.commit("getSubInfos", false);
      }
    },

    urlBase64ToUint8Array(base64String) {
      const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    },
  },
};
</script>

<style scoped lang="scss">
.btn-notification {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: block;
  margin: auto;

  &:hover:not(.active) {
    border-color: #0d6efd;
    background-color: rgba(13, 110, 253, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  }

  &.active {
    background: #0d6efd;
    border-color: #0d6efd;
    color: white;

    .icon-container i {
      color: white;
    }
    .sub-text {
      color: rgba(255, 255, 255, 0.8) !important;
    }
    .status-text {
      color: white;
    }
  }

  .icon-container {
    width: 45px;
    height: 45px;
    background: rgba(13, 110, 253, 0.1);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;

    i {
      font-size: 1.2rem;
      color: #0d6efd;
    }
  }

  .text-container {
    text-align: left;
    display: flex;
    flex-direction: column;

    .status-text {
      font-weight: 700;
      font-size: 1rem;
      color: #2d3436;
    }

    .sub-text {
      font-size: 0.75rem;
      color: #636e72;
    }
  }
}

.shake-animation {
  animation: ring 4s ease infinite;
}

@keyframes ring {
  0% {
    transform: rotate(0);
  }
  1% {
    transform: rotate(30deg);
  }
  3% {
    transform: rotate(-28deg);
  }
  5% {
    transform: rotate(34deg);
  }
  7% {
    transform: rotate(-32deg);
  }
  9% {
    transform: rotate(30deg);
  }
  11% {
    transform: rotate(-28deg);
  }
  13% {
    transform: rotate(26deg);
  }
  15% {
    transform: rotate(-24deg);
  }
  17% {
    transform: rotate(22deg);
  }
  19% {
    transform: rotate(-20deg);
  }
  21% {
    transform: rotate(18deg);
  }
  23% {
    transform: rotate(-16deg);
  }
  25% {
    transform: rotate(14deg);
  }
  27% {
    transform: rotate(-12deg);
  }
  29% {
    transform: rotate(10deg);
  }
  31% {
    transform: rotate(-8deg);
  }
  33% {
    transform: rotate(6deg);
  }
  35% {
    transform: rotate(-4deg);
  }
  37% {
    transform: rotate(2deg);
  }
  39% {
    transform: rotate(-1deg);
  }
  41% {
    transform: rotate(1deg);
  }
  43% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(0);
  }
}
</style>
